// ============================================================================
// useMOMData — Minutes of Meeting hook
// Manages mom_templates (reusable HTML shells) and
// mom_documents (per-task meeting notes) for a given project.
// ============================================================================

import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { formatSupabaseError } from './useSupabase';

// ── Session guard ─────────────────────────────────────────────────────────────
const requireSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw new Error('Authentication error: ' + error.message);
  if (!session?.user) throw new Error('Please sign in to perform this action.');
  return session.user.id;
};

const normalizeId = (id) => (id === null || id === undefined ? null : Number(id));

// ── Default template HTML ─────────────────────────────────────────────────────
export const DEFAULT_TEMPLATE_HTML = `<h3>Agenda &amp; Discussion</h3>
<table>
  <thead>
    <tr>
      <th>#</th><th>Topic</th><th>Discussion</th><th>Action Items</th><th>Responsible</th><th>Deadline</th><th>Status</th>
    </tr>
  </thead>
  <tbody>
    <tr><td>1</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr>
    <tr><td>2</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr>
    <tr><td>3</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr>
  </tbody>
</table>
<h3>Notes</h3>
<p>&nbsp;</p>
<h3>Next Steps</h3>
<ul>
  <li>&nbsp;</li>
</ul>`;

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useMOMData(projectId) {
  const [documents, setDocuments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Synchronously reset state when projectId changes so consumers never see
  // stale documents from the previous project before the fetch begins.
  const [prevProjectId, setPrevProjectId] = useState(projectId);
  if (prevProjectId !== projectId) {
    setPrevProjectId(projectId);
    setDocuments([]);
    setTemplates([]);
    setError(null);
    setLoading(!!projectId);
  }

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [{ data: docs, error: docsErr }, { data: tpls, error: tplsErr }] = await Promise.all([
        supabase
          .from('mom_documents')
          .select('id, project_id, task_key, template_id, title, content_html, created_at, updated_at')
          .eq('project_id', projectId)
          .order('updated_at', { ascending: false }),
        supabase
          .from('mom_templates')
          .select('id, project_id, name, content_html, is_default, created_at')
          .or(`project_id.eq.${projectId},project_id.is.null`)
          .order('created_at', { ascending: true }),
      ]);
      if (docsErr) throw docsErr;
      if (tplsErr) throw tplsErr;
      setDocuments(
        (docs || []).map(d => ({ ...d, id: normalizeId(d.id), project_id: normalizeId(d.project_id) }))
      );
      setTemplates(
        (tpls || []).map(t => ({ ...t, id: normalizeId(t.id), project_id: normalizeId(t.project_id) }))
      );
    } catch (e) {
      setError(formatSupabaseError(e));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Ensure default template ────────────────────────────────────────────────
  // Returns the first template if any exist; otherwise creates and returns a
  // new default template for this project.
  const ensureDefaultTemplate = useCallback(async (currentTemplates) => {
    const list = currentTemplates || templates;
    const existing = list.find(t => t.is_default) || list[0];
    if (existing) return existing;
    try {
      const userId = await requireSession();
      const { data, error: err } = await supabase
        .from('mom_templates')
        .insert({
          project_id: normalizeId(projectId),
          name: 'Default Template',
          content_html: DEFAULT_TEMPLATE_HTML,
          is_default: true,
          created_by: userId,
        })
        .select()
        .single();
      if (err) throw err;
      const tpl = { ...data, id: normalizeId(data.id), project_id: normalizeId(data.project_id) };
      setTemplates(prev => [...prev, tpl]);
      return tpl;
    } catch {
      return null;
    }
  }, [templates, projectId]);

  // ── Documents CRUD ─────────────────────────────────────────────────────────
  const createDocument = useCallback(async ({ taskKey, templateId, title, contentHtml }) => {
    const userId = await requireSession();
    const { data, error: err } = await supabase
      .from('mom_documents')
      .insert({
        project_id: normalizeId(projectId),
        task_key: taskKey,
        template_id: templateId ? normalizeId(templateId) : null,
        title: title || '',
        content_html: contentHtml || '',
        created_by: userId,
      })
      .select()
      .single();
    if (err) throw new Error(formatSupabaseError(err));
    const doc = { ...data, id: normalizeId(data.id), project_id: normalizeId(data.project_id) };
    setDocuments(prev => [doc, ...prev]);
    return doc;
  }, [projectId]);

  const updateDocument = useCallback(async (id, { title, contentHtml, templateId, taskKey }) => {
    await requireSession();
    const patch = { updated_at: new Date().toISOString() };
    if (title !== undefined) patch.title = title;
    if (contentHtml !== undefined) patch.content_html = contentHtml;
    if (templateId !== undefined) patch.template_id = templateId ? normalizeId(templateId) : null;
    if (taskKey !== undefined) patch.task_key = taskKey;
    const { data, error: err } = await supabase
      .from('mom_documents')
      .update(patch)
      .eq('id', normalizeId(id))
      .select()
      .single();
    if (err) throw new Error(formatSupabaseError(err));
    const doc = { ...data, id: normalizeId(data.id), project_id: normalizeId(data.project_id) };
    setDocuments(prev => prev.map(d => normalizeId(d.id) === normalizeId(id) ? doc : d));
    return doc;
  }, []);

  const deleteDocument = useCallback(async (id) => {
    await requireSession();
    const { error: err } = await supabase
      .from('mom_documents')
      .delete()
      .eq('id', normalizeId(id));
    if (err) throw new Error(formatSupabaseError(err));
    setDocuments(prev => prev.filter(d => normalizeId(d.id) !== normalizeId(id)));
  }, []);

  // ── Templates CRUD ─────────────────────────────────────────────────────────
  const createTemplate = useCallback(async ({ name, contentHtml, isDefault = false, isGlobal = false }) => {
    const userId = await requireSession();
    const { data, error: err } = await supabase
      .from('mom_templates')
      .insert({
        project_id: isGlobal ? null : normalizeId(projectId),
        name: name || 'Untitled Template',
        content_html: contentHtml || '',
        is_default: isDefault,
        created_by: userId,
      })
      .select()
      .single();
    if (err) throw new Error(formatSupabaseError(err));
    const tpl = { ...data, id: normalizeId(data.id), project_id: normalizeId(data.project_id) };
    setTemplates(prev => [...prev, tpl]);
    return tpl;
  }, [projectId]);

  const updateTemplate = useCallback(async (id, { name, contentHtml, isDefault }) => {
    await requireSession();
    const patch = { updated_at: new Date().toISOString() };
    if (name !== undefined) patch.name = name;
    if (contentHtml !== undefined) patch.content_html = contentHtml;
    if (isDefault !== undefined) patch.is_default = isDefault;
    const { data, error: err } = await supabase
      .from('mom_templates')
      .update(patch)
      .eq('id', normalizeId(id))
      .select()
      .single();
    if (err) throw new Error(formatSupabaseError(err));
    const tpl = { ...data, id: normalizeId(data.id), project_id: normalizeId(data.project_id) };
    setTemplates(prev => prev.map(t => normalizeId(t.id) === normalizeId(id) ? tpl : t));
    return tpl;
  }, []);

  const deleteTemplate = useCallback(async (id) => {
    await requireSession();
    const { error: err } = await supabase
      .from('mom_templates')
      .delete()
      .eq('id', normalizeId(id));
    if (err) throw new Error(formatSupabaseError(err));
    setTemplates(prev => prev.filter(t => normalizeId(t.id) !== normalizeId(id)));
  }, []);

  return {
    documents,
    templates,
    loading,
    error,
    refresh: fetchAll,
    ensureDefaultTemplate,
    createDocument,
    updateDocument,
    deleteDocument,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  };
}
